import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { fetchMyNetwork } from '@/api/network';
import { AddCompanyPopover } from '@/app/(client)/network/-components/AddCompanyPopover';
import { ConnectConfirmDialog } from '@/app/(client)/network/-components/ConnectConfirmDialog';
import { ExportNetworkDialog } from '@/app/(client)/network/-components/ExportNetworkDialog';
import { NetworkListError } from '@/app/(client)/network/-components/NetworkListError';
import { NetworkViewToggle } from '@/app/(client)/network/-components/NetworkViewToggle';
import { useAmforiNetworkColumn } from '@/app/(client)/network/-hooks/useAmforiNetworkColumn';
import { myNetworkQueryKey } from '@/app/(client)/network/-hooks/useMyNetwork';
import { useNetworkPage } from '@/app/(client)/network/-hooks/useNetworkPage';
import type { NetworkListSearch } from '@/app/(client)/network/-types';
import { networkListSearch } from '@/app/(client)/network/-types';
import { buildFilterParam } from '@/app/(client)/network/-utils';
import { CreateCompanyDialog } from '@/components/shared/CreateCompanyDialog';
import { HelpSheet } from '@/components/shared/HelpSheet';

const NetworkMap = lazy(() =>
  import('@/components/shared/NetworkMap').then(m => ({
    default: m.NetworkMap,
  })),
);

import {
  faCircleQuestion,
  faFileExport,
  faUnlink,
} from '@fortawesome/pro-light-svg-icons';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNetworkMapCompanies } from '@/hooks/useNetworkMapCompanies';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

const NetworkTable = lazy(() =>
  import('@/app/(client)/network/-components/NetworkTable').then(m => ({
    default: m.NetworkTable,
  })),
);

export const Route = createFileRoute(
  '/(client)/network/(pages)/(list)/my-network/',
)({
  validateSearch: networkListSearch,
  loaderDeps: ({ search }) => search as NetworkListSearch,
  loader: ({ deps }) => {
    const filter = deps.filters ? buildFilterParam(deps.filters) : undefined;
    const params = {
      page: deps.page,
      limit: deps.limit,
      ...(deps.sort !== undefined ? { sort: deps.sort } : {}),
      ...(filter !== undefined ? { filter } : {}),
    };
    const networkQs = new URLSearchParams({
      isSupplierNetwork: 'true',
      page: String(deps.page - 1),
      count: String(deps.limit),
      filter: filter ?? '{}',
      ...(deps.sort !== undefined ? { sort: deps.sort } : {}),
    });
    return queryClient.ensureQueryData({
      queryKey: myNetworkQueryKey(params),
      queryFn: () => fetchMyNetwork(networkQs.toString()),
    });
  },
  // Without its own boundary a failed load bubbled to the shell's, which kept
  // showing it after navigating away (the errored match renders once more
  // during the transition and re-throws under the new pathname).
  errorComponent: NetworkListError,
  component: MyNetworkPage,
});

function MyNetworkPage() {
  const { getTranslation: t } = useTranslator({ page: 'network' });
  const amforiLastSyncedByCompanyId = useAmforiNetworkColumn();

  const {
    search,
    setView,
    companies,
    total,
    sorting,
    filters,
    rowSelection,
    setRowSelection,
    helpOpen,
    setHelpOpen,
    filterOptions,
    handleSortingChange,
    handleFiltersChange,
    handlePaginationChange,
    handleCellCommit,
    onExportAll,
    exportDialog,
    bulkActionsBar,
    unlinkDialog,
    recalcDialog,
    addCompany,
    connectConfirmDialog,
    createCompanyDialog,
    rights,
  } = useNetworkPage();

  const networkView = search.view ?? 'table';

  const mapFilter = search.filters
    ? buildFilterParam(search.filters)
    : undefined;
  const mapParams = {
    ...(mapFilter !== undefined ? { filter: mapFilter } : {}),
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
  };
  const { data: mapData, isLoading: mapLoading } =
    useNetworkMapCompanies(mapParams);

  const mapOverlay =
    networkView === 'map' ? (
      <Suspense>
        <NetworkMap companies={mapData?.data ?? []} loading={mapLoading} />
      </Suspense>
    ) : undefined;

  return (
    <>
      <div className='flex-1 min-h-0 flex flex-col overflow-hidden'>
        <div className='flex justify-end mb-2'>
          <NetworkViewToggle value={networkView} onChange={setView} />
        </div>
        <Suspense>
          <NetworkTable
            data={companies}
            total={total}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            pageIndex={search.page - 1}
            pageSize={search.limit}
            onPaginationChange={handlePaginationChange}
            onCellCommit={handleCellCommit}
            readOnly={!rights.canEdit}
            {...(amforiLastSyncedByCompanyId
              ? { amforiLastSyncedByCompanyId }
              : {})}
            {...filterOptions}
            bulkActions={[
              ...(rights.canEdit
                ? [
                    {
                      id: 'unlink' as const,
                      type: 'button' as const,
                      label: t('bulk.unlinkAction'),
                      tooltip: t('bulk.unlink', {
                        label: t('bulk.company', {
                          count: bulkActionsBar.selectedCount,
                        }),
                      }),
                      icon: (
                        <FontAwesomeIcon icon={faUnlink} className='text-sm' />
                      ),
                      onClick: bulkActionsBar.onUnlink,
                      loading: bulkActionsBar.isUnlinking,
                      disabled:
                        bulkActionsBar.isUnlinking ||
                        bulkActionsBar.isExporting ||
                        bulkActionsBar.isRecalculating,
                    },
                  ]
                : []),
              ...(rights.canExport
                ? [
                    {
                      id: 'export' as const,
                      type: 'button' as const,
                      label: t('bulk.exportAction'),
                      tooltip: t('bulk.export', {
                        label: t('bulk.company', {
                          count: bulkActionsBar.selectedCount,
                        }),
                      }),
                      icon: (
                        <FontAwesomeIcon
                          icon={faFileExport}
                          className='text-sm'
                        />
                      ),
                      onClick: bulkActionsBar.onExport,
                      loading: bulkActionsBar.isExporting,
                      disabled:
                        bulkActionsBar.isUnlinking ||
                        bulkActionsBar.isExporting ||
                        bulkActionsBar.isRecalculating,
                    },
                  ]
                : []),
              ...(rights.canRecalculate
                ? [
                    {
                      id: 'recalculate' as const,
                      type: 'button' as const,
                      label: t('bulk.recalculateAction'),
                      tooltip: t('bulk.recalculate', {
                        label: t('bulk.company', {
                          count: bulkActionsBar.selectedCount,
                        }),
                      }),
                      icon: (
                        <FontAwesomeIcon
                          icon={faArrowsRotate}
                          className='text-sm'
                        />
                      ),
                      onClick: bulkActionsBar.onRecalculate,
                      loading: bulkActionsBar.isRecalculating,
                      disabled:
                        bulkActionsBar.isUnlinking ||
                        bulkActionsBar.isExporting ||
                        bulkActionsBar.isRecalculating,
                    },
                  ]
                : []),
            ]}
            toolbarActions={[
              {
                id: 'help',
                type: 'button' as const,
                label: t('table.help'),
                icon: (
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    className='size-3.5'
                  />
                ),
                iconOnly: true as const,
                variant: 'outline' as const,
                onClick: () => setHelpOpen(true),
              },
              ...(rights.canAdd
                ? [
                    {
                      id: 'add-company',
                      type: 'custom' as const,
                      content: (
                        <AddCompanyPopover
                          open={addCompany.open}
                          onOpen={addCompany.onOpen}
                          onClose={addCompany.onClose}
                          query={addCompany.query}
                          onQueryChange={addCompany.setQuery}
                          companies={addCompany.companies}
                          hasNextPage={addCompany.hasNextPage}
                          isFetchingNextPage={addCompany.isFetchingNextPage}
                          isLoading={addCompany.isLoading}
                          onFetchNextPage={addCompany.fetchNextPage}
                          onConnectRequest={addCompany.onConnectRequest}
                          isLinking={addCompany.isLinking}
                          confirmOpen={connectConfirmDialog.open}
                          onCreateCompany={addCompany.onCreateCompany}
                        />
                      ),
                    },
                  ]
                : []),
              ...(rights.canExport
                ? [
                    {
                      id: 'export',
                      type: 'button' as const,
                      label: t('bulk.exportAction'),
                      icon: (
                        <FontAwesomeIcon
                          icon={faFileExport}
                          className='text-sm'
                        />
                      ),
                      variant: 'outline' as const,
                      onClick: onExportAll,
                      loading: bulkActionsBar.isExporting,
                    },
                  ]
                : []),
            ]}
            overlay={mapOverlay}
          />
        </Suspense>
      </div>

      <HelpSheet
        articleId='8508491-build-your-network-find-add-or-unlink-companies-and-suppliers-to-from-your-network'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      {rights.canAdd && (
        <CreateCompanyDialog
          open={createCompanyDialog.open}
          onOpenChange={createCompanyDialog.onOpenChange}
          onCreated={createCompanyDialog.onCreated}
          onConnectExisting={createCompanyDialog.onConnectExisting}
          isLinking={createCompanyDialog.isLinking}
          connectionType='SUPPLIER'
          showConnectionType
        />
      )}

      {/* Unlink confirmation */}
      <AlertDialog
        open={unlinkDialog.open}
        loading={unlinkDialog.isPending}
        onOpenChange={v => !v && unlinkDialog.onClose()}
      >
        <AlertDialogContent>
          <AlertDialogHeader className='flex flex-col gap-3'>
            <div className='flex items-center gap-2.5'>
              <span className='flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 shrink-0'>
                <FontAwesomeIcon
                  icon={faUnlink}
                  className='text-destructive text-sm'
                />
              </span>
              <AlertDialogTitle>
                {unlinkDialog.selectedCount === 1
                  ? t('unlink.title_one')
                  : t('unlink.title_other', {
                      count: unlinkDialog.selectedCount,
                    })}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              <span className='space-y-3 flex flex-col'>
                <p>
                  {t('unlink.description1')}{' '}
                  <strong className='text-foreground'>
                    {unlinkDialog.selectedCount === 1
                      ? unlinkDialog.companyName ||
                        t('unlink.descriptionCompany_one')
                      : t('unlink.descriptionCompany_other')}
                  </strong>
                  {t('unlink.description2')}
                </p>
                <p>
                  {t('unlink.suggestion')}{' '}
                  <strong className='text-foreground'>
                    {t('unlink.collaborationStatus')}
                  </strong>{' '}
                  {t('unlink.suggestionSuffix')}
                </p>
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unlinkDialog.isPending}>
              {t('unlink.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={unlinkDialog.onConfirm}
              loading={unlinkDialog.isPending}
            >
              {unlinkDialog.isPending
                ? t('unlink.pending')
                : t('unlink.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recalculate confirmation */}
      <AlertDialog
        open={recalcDialog.open}
        loading={recalcDialog.isPending}
        onOpenChange={v => !v && recalcDialog.onClose()}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recalc.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('recalc.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={recalcDialog.isPending}>
              {t('recalc.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant='default'
              onClick={recalcDialog.onConfirm}
              loading={recalcDialog.isPending}
            >
              {recalcDialog.isPending
                ? t('recalc.pending')
                : t('recalc.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExportNetworkDialog state={exportDialog} />

      <ConnectConfirmDialog
        open={connectConfirmDialog.open}
        company={connectConfirmDialog.company}
        connectionType={connectConfirmDialog.connectionType}
        isPending={connectConfirmDialog.isPending}
        onClose={connectConfirmDialog.onClose}
        onConfirm={connectConfirmDialog.onConfirm}
      />
    </>
  );
}
